import React,{useState,useEffect} from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';
import { trackButtonClick } from '../utils/tracking';

const {FiShield,FiHeart,FiDollarSign,FiClock,FiCheck,FiStar,FiMapPin}=FiIcons;

const LandingPage=()=> {
  const [currentLocation,setCurrentLocation]=useState('California');
  const benefits=[
    {icon: FiShield,title: 'Comprehensive Coverage',description: 'Accidents,illnesses,hereditary conditions,and more'},
    {icon: FiDollarSign,title: 'Up to 90% Reimbursement',description: 'Higher reimbursement rates than most competitors'},
    {icon: FiHeart,title: 'No Breed or Age Restrictions',description: 'Coverage for dogs,cats,birds,and exotic pets - all ages welcome'},
    {icon: FiClock,title: 'Fast Claims Processing',description: 'Get reimbursed in 10-30 days with easy app-based claims'}
  ];
  const testimonials=[
    {name: 'Sarah M.',location: 'Los Angeles,CA',rating: 5,text: 'Nationwide saved me thousands when my Golden Retriever needed emergency surgery. The claim process was so easy!'},
    {name: 'Mike D.',location: 'San Francisco,CA',rating: 5,text: 'Best decision I made for my cat. The wellness plan covers all routine care and the customer service is excellent.'},
    {name: 'Jennifer L.',location: 'San Diego,CA',rating: 5,text: 'My exotic bird needed specialized care and Nationwide covered it all. No other company would insure my pet.'}
  ];
  const faqItems=[
    {question: 'What does Nationwide pet insurance cover in California?',answer: 'Nationwide covers accidents,illnesses,hereditary conditions,cancer,chronic diseases,diagnostics,surgeries,medications,and even alternative therapies like acupuncture. Optional wellness plans cover routine care with 100% reimbursement up to annual limits ($250-$450).'},
    {question: 'How much does pet insurance cost in California?',answer: 'Plans start as low as $13/month for basic coverage. Average costs are around $61/month for dogs and $25/month for cats,varying by age,breed,and location within California. Multi-pet discounts available.'},
    {question: 'Are there waiting periods for coverage?',answer: '14 days for accidents and illnesses,6 months for orthopedic conditions like cruciate ligaments (waivable with exam),and 12 months for hip dysplasia in some breeds.'},
    {question: 'Can I use any veterinarian in California?',answer: 'Yes! Nationwide works with all licensed veterinarians throughout California. You can visit any vet from Los Angeles to San Francisco to Sacramento,including specialists and emergency clinics.'}
  ];

  useEffect(()=> {
    // Simple location detection simulation
    const locations=['Los Angeles','San Francisco','San Diego','Sacramento','San Jose'];
    const randomLocation=locations[Math.floor(Math.random() * locations.length)];
    setCurrentLocation(randomLocation);
  },[]);

  return (
    <div className="overflow-hidden">
      <SEOHead
        title="Pet Insurance California - Nationwide Coverage | Burbank Insurance Services"
        description="Get pet insurance in California. Nationwide coverage for dogs,cats & exotic pets. Compare plans,get quotes & protect your pets today with America's #1 pet insurer."
        keywords="pet insurance california,dog insurance,cat insurance,nationwide pet insurance,burbank insurance services,pet insurance quotes"
        canonicalUrl="https://burbankinsurance.com/"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "InsuranceAgency",
          "name": "Burbank Insurance Services - Nationwide Pet Insurance",
          "description": "Authorized Nationwide pet insurance agent serving all of California",
          "url": "https://burbankinsurance.com",
          "areaServed": {
            "@type": "State",
            "name": "California"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Pet Insurance Plans",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Dog Insurance",
                  "description": "Comprehensive dog insurance coverage in California"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Cat Insurance",
                  "description": "Complete cat insurance coverage in California"
                }
              }
            ]
          }
        }}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <SafeIcon icon={FiMapPin} className="h-5 w-5 text-blue-300" />
                <span className="text-blue-200">Serving {currentLocation} & All California</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                America's #1 Pet Insurance Provider
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Instant quotes for dogs,cats,birds & exotic pets. Up to 90% reimbursement with no age limits.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick('Hero Get Free Quote Now')}
                  className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors text-center"
                >
                  Get Free Quote Now
                </a>
                <Link
                  to="/compare"
                  onClick={() => trackButtonClick('Hero Compare Plans')}
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-colors text-center"
                >
                  Compare Plans
                </Link>
              </div>
              <div className="mt-8 flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold">#1</div>
                  <div className="text-sm text-blue-200">Pet Insurer</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">90%</div>
                  <div className="text-sm text-blue-200">Max Reimbursement</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">$13</div>
                  <div className="text-sm text-blue-200">Starting Price</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=600&h=400&fit=crop&crop=center"
                alt="Happy family with golden retriever in California park - Nationwide Pet Insurance"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white text-gray-900 p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500" />
                  <span className="font-semibold">Protected in California</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Nationwide Pet Insurance in California?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              America's #1 pet insurance provider protecting California pets from San Diego to Sacramento with comprehensive coverage and exceptional service.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit,index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Coverage for California Pets
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Accidents & Injuries</h3>
                    <p className="text-gray-600">Emergency care,surgery,hospitalization,medications,and exam fees up to $5,000/year</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Illnesses & Chronic Conditions</h3>
                    <p className="text-gray-600">Cancer,diabetes,hereditary conditions,diagnostics,and alternative therapies</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Wellness Care (Optional)</h3>
                    <p className="text-gray-600">Preventive care,vaccinations,spay/neuter,dental cleanings - 100% reimbursement up to $250-$450 annually</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Unique Perks</h3>
                    <p className="text-gray-600">Behavioral therapy,lost pet recovery ($5,000),boarding coverage if hospitalized</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiCheck} className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Birds & Exotic Pets</h3>
                    <p className="text-gray-600">Only major provider covering birds,rabbits,reptiles,and other exotic pets</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <a
                  href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick('Coverage Details Start Your Quote')}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block"
                >
                  Start Your Quote
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1576201836106-db1758fd1c97?w=600&h=400&fit=crop&crop=center"
                alt="Veterinarian examining cat in California clinic"
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What California Pet Owners Say
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from satisfied customers across the Golden State
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial,index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-lg"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_,i) => (
                    <SafeIcon key={i} icon={FiStar} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about pet insurance in California
            </p>
          </motion.div>
          <div className="space-y-8">
            {faqItems.map((item,index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.question}</h3>
                <p className="text-gray-700">{item.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Protect Your California Pet?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Join thousands of California pet owners who trust America's #1 pet insurance provider for their pet's health and safety.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackButtonClick('Footer CTA Get Instant Quote')}
                className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                Get Instant Quote
              </a>
              <Link
                to="/compare"
                onClick={() => trackButtonClick('Footer CTA Compare All Plans')}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-colors"
              >
                Compare All Plans
              </Link>
            </div>
            <p className="mt-6 text-blue-200">
              Free quotes • No obligation • Instant coverage available
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
export default LandingPage;